import FingerprintJS from '@fingerprintjs/fingerprintjs'
import getPreferences from 'api/getPreferences'
import login from 'api/login'
import { PlausibleEvent } from 'enums/plausible'
import { Formik } from 'formik'
import { useCallback, useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import useAntifraudSDK from 'utils/hooks/useAntifraudSDK'
import useLanguageForSave from 'utils/hooks/useLanguageForSave'
import usePlausible from 'utils/hooks/usePlausible'
import setTokensAndRedirect from 'utils/setTokensAndRedirect'
import * as yup from 'yup'
import ErrorCode from './ErrorCodes'
import handleResponseError from './handleResponseError'
import validateMessage from './validateMessage'

import { useTranslation } from 'next-i18next'

import ErrorMessage from 'components/ErrorMessage/ErrorMessage'
import Field from 'components/Form/Field/Field'
import Form from 'components/Form/Form'
import Password from 'components/Form/Password/Password'
import { BanIcon, ErrorIcon, TimesRoundIcon, WiFiIcon } from 'components/Images/Icons'
import Link from 'components/Link'
import SubmitButton from 'components/SubmitButton'

import styles from './LoginForm.module.scss'

const LoginForm = ({ referrer, error, setError, setSocialLoginError }) => {
  const [t, i18n] = useTranslation(['main', 'error'])
  const setEvent = usePlausible()
  const savedLanguage = useLanguageForSave(i18n)
  const { initAntifraudSession } = useAntifraudSDK()

  const [isLoading, toggleIsLoading] = useState(false)
  const [isSucceed, toggleIsSucceed] = useState(false)
  const [isCaptchaEnabled, setIsCaptchaEnabled] = useState()
  const [email, setEmail] = useState('')
  const captchaRef = useRef(null)

  useEffect(() => {
    const getAppPreferences = async () => {
      try {
        const res = await getPreferences()
        setIsCaptchaEnabled(res?.data?.payload?.googleRecaptchaLoginEnable)
      } catch (err) {
        console.error(err)
      }
    }
    getAppPreferences()
  }, [isCaptchaEnabled])

  const onLogin = useCallback(
    async (values) => {
      setSocialLoginError('')
      toggleIsLoading(true)
      setEvent(PlausibleEvent.ClickedSignIn, {
        step: 2,
        referrer,
        email: values.email,
      })

      let captcha = null
      try {
        if (isCaptchaEnabled && captchaRef.current) {
          captcha = await captchaRef.current.executeAsync()
        }

        const sessionId = await initAntifraudSession(`login-${Date.now()}`, 'login')

        const fp = await FingerprintJS.load()
        const fpResponse = await fp.get()

        const res = await login({
          email: values.email,
          password: values.password,
          visitorId: fpResponse.visitorId,
          language: savedLanguage,
          captcha: isCaptchaEnabled ? captcha : undefined,
          sessionId: sessionId || undefined,
        })

        if (res.data.status !== 'success') {
          const { code, payload } = res.data

          switch (code) {
            case ErrorCode.VALIDATION_ERRORS:
              if (payload?.errors) {
                const wrappedErrors = handleResponseError(res)
                setError({ message: wrappedErrors })
              }
              break
            case ErrorCode.INVALID_CREDENTIALS:
            case ErrorCode.USER_WITH_SPECIFIED_EMAIL_NOT_FOUND:
              setError({ icon: TimesRoundIcon, message: validateMessage.invalidCredentials })
              break
            case ErrorCode.SOCIAL_DUPLICATE_USER_EMAIL:
              setError({ message: validateMessage.duplicateUserEmail })
              break
            case ErrorCode.USER_BANNED:
              setError({ icon: BanIcon, message: validateMessage.banned })
              break
            case ErrorCode.USER_BLOCKED:
              setError({ icon: ErrorIcon, message: validateMessage.userBlocked })
              break
            default:
              setError({ icon: WiFiIcon, message: validateMessage.serverError })
          }

          setEvent(PlausibleEvent.NotAuthorised, {
            step: 3,
            referrer,
            type: `system`,
            error: ErrorCode[code] || 'unknown',
            email: values.email,
          })

          if (isCaptchaEnabled && captchaRef.current) {
            await captchaRef.current.reset()
          }
        } else {
          toggleIsSucceed(true)
          setError(undefined)
          // added await in order to wait for the event to be sent before redirect
          await setEvent(
            PlausibleEvent.Authorised,
            { step: 3, referrer, type: `system`, email: values.email },
            true,
          )

          setTokensAndRedirect(res.data.payload.accessToken, res.data.payload.refreshToken)
        }
      } catch (e) {
        console.error(e)
        setError({ icon: WiFiIcon, message: validateMessage.serverError })
        setEvent(PlausibleEvent.NotAuthorised, {
          step: 3,
          referrer,
          type: `system`,
          error: validateMessage.serverError,
          email: values.email,
        })

        if (isCaptchaEnabled && captchaRef.current) {
          await captchaRef.current.reset()
        }
      } finally {
        toggleIsLoading(false)
      }
    },
    [setError, savedLanguage, isCaptchaEnabled, captchaRef.current, initAntifraudSession],
  )
  useEffect(() => {
    localStorage.setItem('saved_email', email)
  }, [email])
  const emailRef = useCallback(
    (node) => {
      if (node) {
        node.addEventListener('change', (e) => {
          setEmail(e.target.value)
        })
      }
    },
    [setEmail],
  )

  return (
    <>
      <Formik
        initialValues={{ email: '', password: '' }}
        validateOnChange
        validateOnBlur={false}
        validationSchema={yup.object({
          email: yup
            .string()
            .email(t('error:emailNotValid'))
            .required(t('error:requiredField'))
            .max(49, t('error:maxLenSymbols', { length: 49 })),
          password: yup
            .string()
            .min(8, t('error:passwordMinChar'))
            .required(t('error:requiredField')),
        })}
        onSubmit={onLogin}
      >
        <Form>
          <Field
            innerRef={emailRef}
            placeholder={t('main:authEnterEmail')}
            name="email"
            type="email"
            id="login-form-field-input"
          />
          <Password
            referrer={referrer}
            placeholder={t('main:authEnterPassword')}
            icon
            name="password"
            id="login-form-password-input"
          />
          {error && <ErrorMessage icon={error.icon} message={error.message} />}

          <div className={styles.submit}>
            <SubmitButton id="login-form-submit-button" isLoading={isLoading} isSucceed={isSucceed}>
              {t('main:signUpLogin').toString()}
            </SubmitButton>

            <div className={styles.register}>
              <span>{t('main:authDontHaveAcc')}</span>
              <Link
                href="/auth/signup?from=loginPage"
                as="/auth/signup"
                id="login-form-signUp-link"
              >
                {t('main:authCreateAcc')}
              </Link>
            </div>
          </div>
        </Form>
      </Formik>
      {isCaptchaEnabled && (
        <ReCAPTCHA sitekey={process.env.NEXT_PUBLIC_RECAPTCHA} ref={captchaRef} size="invisible" />
      )}
    </>
  )
}

export default LoginForm
